<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import axios from "axios";
import { authHeader } from "../../../helpers/auth-header";

import ConfirmationModal from "@/components/modals/confirmation-modal";
import MessageModal from "@/components/modals/message-modal";

import InputGroup from "@/components/forms/input-group";
import SelectGroup from "@/components/forms/select-group";
import SaveButton from "@/components/buttons/save-button";

/**
 * Orders Component
 */
export default {
    components: {
        Layout,
        PageHeader,
        ConfirmationModal,
        MessageModal,
        InputGroup,
        SelectGroup,
        SaveButton,
    },
    data() {
        const id = this.$route.params.id;
        const modeName = id === 'add' ? 'Add' : 'Edit';

        return {
            id: id,
            title: `Treatment ${modeName}`,
            modeName,
            items: [
                {
                    text: "Treatment"
                },
                {
                    text: modeName,
                    active: true,
                },
            ],

            tabs: ['info', 'timeslot'],
            products: [],

            data: {
                name: '',
                sku: '',
                duration: 0,
                category: [],
                branch_id: [],
                timeslot_cat: '',
                commission_type: 'fixed_amount',
                commission: 0,
                status: 1,
                shopify_product_id: '',
                price: 0,
                is_stackable: 0,
                weekdayTimeslots: [],
                weekdayTimeslotInputs: [''],
                weekendTimeslots: [],
                weekendTimeslotInputs: [''],
                selectedStaff: [],
            },
            statusOptions: [
                { name: 'Active', value: 1 },
                { name: 'Inactive', value: 0 },
            ],

            commissionOptions: [
                { name: 'Fixed Amount', value: 'fixed_amount' },
                { name: 'Percentage', value: 'percentage' },
            ],

            branchOptions: [],
            categoryOptions: [],
            timeslotCategoryOptions: [],
            staffOptions: [],

            errors: {},            
        };
    },
    async created() {
        //
    },
    computed: {
        weekdayTimeslotInputs() {
            return this.data.weekdayTimeslots.slice();
        },
        weekendTimeslotInputs() {
            return this.data.weekendTimeslots.slice();
        },
        selectAll: {
            get () {
                return this.data.selectedStaff.length === this.staffOptions.length;
            },
            set (value) {
                this.data.selectedStaff = [];

                if (value) {
                    this.staffOptions.forEach((staff) => {
                        this.data.selectedStaff.push(staff.id)
                    })
                }
            }
        },
    },
    async mounted() {
        this.retrieveBranches().then((branches) => {
            this.branchOptions = branches.map((branch) => ({
                name: branch.name,
                value: branch.id,
            }));
        });
        
        this.retrieveCategories().then((categories) => {
            this.categoryOptions = categories.map((category) => ({
                name: category.display_name,
                value: category.id,
            }));
        });

        this.retrieveTimeslotCategories().then((timeslot_categories) => {
            this.timeslotCategoryOptions = timeslot_categories.map((timeslot_category) => ({
                name: timeslot_category.name,
                value: timeslot_category.id,
            }));
        });

        this.retrieveStaffs().then((staffs) => {
            this.staffOptions = staffs.map((staff) => ({
                name: staff.name,
                id: staff.id,
            }));

            if (this.modeName === 'Add') {
                this.selectAll = true;
            }
        });
        
        if (this.modeName !== 'Add') {
            const result = await axios.get(`${process.env.VUE_APP_APIURL}/product/${this.id}`, {
                headers: authHeader(),
            }); 

            if (result && result.data['code'] === 'success') {
                const data = result.data['data']['product'];
                for (const [key, item] of Object.entries(data)) {
                    if (key in this.data) {
                        this.data[key] = item;
                    }
                }

                const descriptions = result.data['data']['product']['description'];
                for (const [key, item] of Object.entries(descriptions)) {
                    if (key in this.data) {
                        this.data[key] = item;
                    }
                }

                this.data.commission_type = result.data['data']['product']['description']['commission_type'];

                // for pre-select the categories
                result.data['data']['product']['categories'].forEach((categoryRecord) => {
                    this.data.category.push(categoryRecord.id);
                });

                // for pre-select the branches
                result.data['data']['product']['commission'].forEach((branchRecord) => {
                    this.data.branch_id.push(branchRecord.branch_id);
                });

                const timeslot_cat = result.data['data']['product']['timeslot_cat'][0].timeslot_category_id;
                this.data['timeslot_cat'] = timeslot_cat;

                this.data.weekdayTimeslots = [];
                this.data.weekendTimeslots = [];
                const timeslotsData = JSON.parse(result.data['data']['product']['timeslot_cat'][0].timeslots);
                if (timeslotsData) {
                    for (const [key, value] of Object.entries(timeslotsData)) {
                        if (key.startsWith('weekday_')) {
                            this.data.weekdayTimeslots.push(value);
                        } else if (key.startsWith('weekend_')) {
                            this.data.weekendTimeslots.push(value);
                        }
                    }
                }

                // for pre-select the staffs
                result.data['data']['product']['staffs'].forEach((staffRecord) => {
                    this.data.selectedStaff.push(staffRecord.staff_id);
                });
            }
        }
    },
    watch: {
        //
    },
    methods: {
        async retrieveBranches() {
            this.loading = true;
            let branches = [];

            try {
                const item = await axios.get(`${process.env.VUE_APP_APIURL}/branch`, {
                    headers: authHeader(),
                });

                const result = await item.data;
                if (result['code'] === 'success') {
                    branches = result['data'].branches;
                }
            } catch (e) {
                console.error(e);
            }

            this.loading = false;
            return branches;
        },

        async retrieveCategories() {
            this.loading = true;
            let categories = [];

            try {
                const item = await axios.get(`${process.env.VUE_APP_APIURL}/category`, {
                    headers: authHeader(),
                });

                const result = await item.data;
                if (result['code'] === 'success') {
                    const categoryDetails = result['data'].categories;
                    categories = categoryDetails.filter(category => category.status === 1);
                }
            } catch (e) {
                console.error(e);
            }

            this.loading = false;
            return categories;
        },

        async retrieveTimeslotCategories() {
            this.loading = true;
            let timeslot_categories = [];

            try {
                const item = await axios.get(`${process.env.VUE_APP_APIURL}/timeslot-category`, {
                    headers: authHeader(),
                });

                const result = await item.data;
                if (result['code'] === 'success') {
                    const timeslotCategoryDetails = result['data'].timeslot_categories;
                    timeslot_categories = timeslotCategoryDetails.filter(category => category.status === 1);
                }
            } catch (e) {
                console.error(e);
            }

            this.loading = false;
            return timeslot_categories;
        },

        async retrieveStaffs() {
            this.loading = true;
            let staffs = [];

            try {
                const item = await axios.get(`${process.env.VUE_APP_APIURL}/staff`, {
                    headers: authHeader(),
                });

                const result = await item.data;
                if (result['code'] === 'success') {
                    staffs = result['data'].staffs;
                }
            } catch (e) {
                console.error(e);
            }

            this.loading = false;
            return staffs;
        },

        async save() {
            this.errors = {};
            let result;

            const modifiedWeekdayTimeslots = this.data.weekdayTimeslots.map((timeslot, index) => ({
                weekday: 'weekday_' + (index + 1),
                timeslot: this.formatTimeslot(timeslot),
            }));

            const modifiedWeekendTimeslots = this.data.weekendTimeslots.map((timeslot, index) => ({
                weekend: 'weekend_' + (index + 1),
                timeslot: this.formatTimeslot(timeslot),
            }));

            const requestData = {
                ...this.data,
                weekdayTimeslots: JSON.stringify(modifiedWeekdayTimeslots),
                weekendTimeslots: JSON.stringify(modifiedWeekendTimeslots),
            };

            if (this.modeName === 'Add') {
                result = await axios.post(`${process.env.VUE_APP_APIURL}/product`, requestData, {
                    headers: authHeader(),
                });
            } else {
                result = await axios.put(`${process.env.VUE_APP_APIURL}/product/${this.id}`, requestData, {
                    headers: authHeader(),
                });
            }

            if (result) {
                if (result.data['code'] === 'success') {
                    if (this.modeName === 'Add') {
                        this.$router.back();
                    } else {
                        this.$refs.messageModal.showModal('Your record has been updated successfully');
                    }
                } else if (result.data['code'] === 'invalid_field') {
                    this.errors = result.data['errors'];
                }
            }
        },

        addWeekdayTimeslot() {
            this.data.weekdayTimeslots.push('');
            this.data.weekdayTimeslotInputs.push('');
        },

        updateWeekdayTimeslot(value, index) {
            this.$set(this.data.weekdayTimeslots, index, value);
        },

        addWeekendTimeslot() {
            this.data.weekendTimeslots.push('');
            this.data.weekendTimeslotInputs.push('');
        },

        updateWeekendTimeslot(value, index) {
            this.$set(this.data.weekendTimeslots, index, value);
        },

        removeWeekdayTimeslot(index) {
            this.data.weekdayTimeslots.splice(index, 1);
            this.data.weekdayTimeslotInputs.splice(index, 1);
        },

        removeWeekendTimeslot(index) {
            this.data.weekendTimeslots.splice(index, 1);
            this.data.weekendTimeslotInputs.splice(index, 1);
        },

        formatTimeslot(timeslot) {
            if (/^\d{2}:\d{2}:\d{2}$/.test(timeslot)) {
                return timeslot;
            } else if (/^\d{2}:\d{2}$/.test(timeslot)) {
                return timeslot + ':00';
            }
        },
    }
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <ConfirmationModal ref="confirmationModal" />
        <MessageModal ref="messageModal" />
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body p-3">
                        <!-- <b-tabs content-class="mt-3"> -->
                            <!-- <b-tab title="Info" active> -->
                                <div class="row">
                                    <div class="col-12 col-md-8 col-lg-12">
                                        <div class="row">
                                            <div class="col-12 col-md-4 mb-2">
                                                <InputGroup type="text" id="name" displayName="Name*" v-model="data.name" :error="errors.name" />
                                            </div>
                                            <div class="col-12 col-md-4 mb-2">
                                                <InputGroup type="text" id="sku" displayName="SKU* (Need to match with Shopify)" v-model="data.sku" :error="errors.sku" />
                                            </div>
                                            <div class="col-12 col-md-4 mb-2">
                                                <InputGroup type="text" id="duration" displayName="Duration*" v-model="data.duration" :error="errors.duration" />
                                            </div>
                                            <div class="col-12 col-md-4 mb-2">
                                                <SelectGroup id="timeslot_cat" displayName="Timeslot Category*" v-model="data.timeslot_cat" :error="errors.timeslot_cat" :options="timeslotCategoryOptions" />
                                            </div>
                                            <div class="col-12 col-md-4 mb-2">
                                                <InputGroup type="text" id="shopify_product_id" displayName="Shopify Treatment ID/Variant ID* (Need to match with Shopify)" v-model="data.shopify_product_id" :error="errors.shopify_product_id" />
                                            </div>
                                            <div class="col-12 col-md-4 mb-2">
                                                <SelectGroup id="status" displayName="Status*" v-model="data.status" :error="errors.status" :options="statusOptions" />
                                            </div>
                                            <div class="col-12 col-md-4 mb-2">
                                                <SelectGroup id="commission_type" displayName="Commission Type*" v-model="data.commission_type" :error="errors.commission_type" :options="commissionOptions" />
                                            </div>
                                            <div class="col-12 col-md-4 mb-2">
                                                <InputGroup type="text" id="commission" displayName="Commission*" v-model="data.commission" :error="errors.commission" />
                                            </div>
                                            <div class="col-12 col-md-4 mb-2">
                                                <InputGroup type="text" id="price" displayName="Price*" v-model="data.price" :error="errors.price" />
                                            </div>

                                            <div class="col-12 mb-2">
                                                <label for="stackable">Is treatment stackable?</label><br>
                                                <input type="checkbox" id="stackable" v-model="data.is_stackable" :value="data.is_stackable">
                                                &nbsp;<label for="stackable">Yes</label>
                                                <hr>
                                            </div>
                                            
                                            <div class="col-12 mb-2">
                                                <label>Category*</label>
                                                <div v-for="category in categoryOptions" :key="category.value">
                                                    <input type="checkbox" :id="`category_${category.value}`" v-model="data.category" :value="category.value">
                                                    &nbsp;<label :for="`category_${category.value}`">{{ category.name }}</label>
                                                </div>
                                                <div class="alert alert-danger mt-3" role="alert" v-if="this.errors.category && this.errors.category.length > 0">
                                                    {{ this.errors.category[0] }}
                                                </div>
                                                <hr>
                                            </div>

                                            <div class="col-12 mb-2">
                                                <label>Branch</label>
                                                <div v-for="branch in branchOptions" :key="branch.value">
                                                    <input type="checkbox" :id="`branch_${branch.value}`" v-model="data.branch_id" :value="branch.value">
                                                    &nbsp;<label :for="`branch_${branch.value}`">{{ branch.name }}</label>
                                                </div>
                                                <div class="alert alert-danger mt-3" role="alert" v-if="this.errors.branch_id && this.errors.branch_id.length > 0">
                                                    {{ this.errors.branch_id[0] }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            <!-- </b-tab> -->
                            <!-- <b-tab title="Timeslot">
                                <button class="add_timeslot btn btn-outline-secondary mb-3" @click="addWeekdayTimeslot">Add Weekday Timeslot</button>
                                <button class="add_timeslot btn btn-outline-secondary mb-3" @click="addWeekendTimeslot">Add Weekend Timeslot</button>
                                <div class="row">
                                    <div class="col-12" v-for="(timeslot, index) in data.weekdayTimeslots" :key="'weekday_' + index">
                                        <div class="row" v-if="index === 0">
                                            <div class="col-12">
                                                <label :for="`weekday_${index}`">Weekday Timeslots</label>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-3">
                                                <input type="time" class="form-control mb-2" :id="`weekday_${index}`" v-model="data.weekdayTimeslots[index]" @input="updateWeekdayTimeslot($event.target.value, index)" />
                                            </div>
                                            <button class="col-1 btn btn-danger ml-3 h-100" @click="removeWeekdayTimeslot(index)">Remove</button>
                                        </div>
                                    </div>
                                    <div class="col-12">
                                        <hr />
                                    </div>
                                    <div class="col-12" v-for="(timeslot, index) in data.weekendTimeslots" :key="'weekend_' + index">
                                        <div class="row" v-if="index === 0">
                                            <div class="col-12">
                                                <label :for="`weekend_${index}`">Weekend Timeslots</label>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-3">
                                                <input type="time" class="form-control mb-2" :id="`weekend_${index}`" v-model="data.weekendTimeslots[index]" @input="updateWeekendTimeslot($event.target.value, index)" />
                                            </div>
                                            <button class="col-1 btn btn-danger ml-3 h-100" @click="removeWeekendTimeslot(index)">Remove</button>
                                        </div>                             
                                    </div>
                                </div>
                            </b-tab> -->
                            <!-- <b-tab title="Staff">
                                <div class="row">
                                    <div class="col-12">
                                        <div class="alert alert-danger mt-3" role="alert" v-if="this.errors.selectedStaff && this.errors.selectedStaff.length > 0">
                                            {{ this.errors.selectedStaff[0] }}
                                        </div>
                                        <div class="table-container" style="max-height: 80vh; overflow-y: auto;">
                                            <table class="table">
                                                <thead>
                                                    <tr>
                                                        <th><input type="checkbox" id="check_all" v-model="selectAll"></th>
                                                        <th>Staff ID</th>
                                                        <th>Name</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="staff in staffOptions" v-bind:key="staff.id">
                                                        <td><input type="checkbox" :id="staff.id" :value="staff.id" v-model="data.selectedStaff"></td>
                                                        <td>{{ staff.id }}</td>
                                                        <td>{{ staff.name }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </b-tab> -->
                        <!-- </b-tabs> -->
                        <div class="row mt-2">
                            <div class="col-12">
                                <SaveButton @click="save" class="mt-5" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>